import {
  Box,
  Col,
  DSIconButton,
  DSIconChevronDown,
  Row,
  css,
  DSListTile,
  DSShortcutKey,
} from "@cdx/ds";
import {Card} from "../../cdx-models/Card";
import {Project} from "../../cdx-models/Project";
import {NoneButton, SharedPropChangeOverlayForChild} from "./shared";
import {sortBy} from "lodash";
import {Account} from "../../cdx-models/Account";
import {Milestone} from "../../cdx-models/Milestone";
import {prettyShortWorkdays, toCmpDate} from "../../lib/date-utils";
import {Root} from "../../cdx-models/Root";
import {getMySelectedProjectIds} from "../../lib/hooks/useSelectedProjects";
import {TooltipForChild} from "@cdx/common";
import {
  ThemedMilestoneIcon,
  getMilestoneState,
  msToDate,
  setMsThemeColor,
} from "../../features/milestones/milestone-utils";
import {MilestoneTooltipForChild} from "../../pages/milestones/MilestoneTooltip";
import Helper from "../Helper";
import {Query} from "../../cdx-models/utils/MakeModel";

const msHelper = (
  <Helper
    heading={
      <>
        What are Milstones? <DSShortcutKey size="lg">m</DSShortcutKey>
      </>
    }
  >
    <p>
      <i>Milestones</i> represent project goals tied to a specific target date. These are often used
      to represent game releases, patches, publisher deliverables or bigger team intitatives.
    </p>

    <p>Milestones, Runs and Due Dates can be used in any combination.</p>
  </Helper>
);

type MsListOpts = {
  constraints: Query<Milestone>;
  account: Account;
  currentMs: Milestone | null;
  maxCount: number;
};

const getMsList = ({constraints, account, currentMs, maxCount}: MsListOpts) => {
  const nextMs = account.$meta.find("milestones", {
    date: {op: "gte", value: toCmpDate(new Date())},
    isDeleted: false,
    ...constraints,
    $order: "date",
    $limit: maxCount,
  });
  if (!currentMs || currentMs.$meta.isDeleted() || nextMs.some((ms) => ms.id === currentMs.id)) {
    return nextMs;
  } else {
    return sortBy([currentMs, ...nextMs].slice(0, maxCount), [
      (ms) => ms.date.year,
      (ms) => ms.date.month,
      (ms) => ms.date.day,
    ]);
  }
};

type MilestonePropPickerProps = {
  card: Card;
  onChange: (props: any) => unknown;
  project?: Project;
  root: Root;
};
const MilestonePropPicker = (props: MilestonePropPickerProps) => {
  const {card, onChange, root, project} = props;
  const projectIds = project
    ? [project.$meta.get("id", null)!].filter(Boolean)
    : getMySelectedProjectIds(root);
  const msList = getMsList({
    constraints: projectIds.length > 0 ? {milestoneProjects: {projectId: projectIds}} : {},
    account: root.account,
    currentMs: card.milestone,
    maxCount: 3,
  });

  return (
    <Col sp="12px" pt="16px" pb="24px">
      <Row align="center" sp="8px">
        <Box as="h3" size={12} bold color="primary" textTransform="uppercase">
          Milestone
        </Box>
        {msHelper}
        <SharedPropChangeOverlayForChild
          card={card}
          onChange={onChange}
          cardContainerKey="sidebarMilestonePicker"
          availableShortcuts={["milestone"]}
        >
          <TooltipForChild
            tooltip={
              <Box pa="8px">
                Quick select <DSShortcutKey>m</DSShortcutKey>
              </Box>
            }
          >
            <DSIconButton
              variant="secondary"
              size="sm"
              className={css({ml: "auto"})}
              label="Pick"
              icon={<DSIconChevronDown />}
              iconPosition="right"
            />
          </TooltipForChild>
        </SharedPropChangeOverlayForChild>
        <NoneButton
          onClick={() => onChange({milestoneId: null})}
          active={card.milestone === null}
        />
      </Row>
      {msList.length === 0 ? (
        <Box size={14} color="secondary">
          No upcoming milestones
        </Box>
      ) : (
        <Col sp="4px">
          {msList.map((ms) => (
            <MilestoneTooltipForChild key={ms.id} milestone={ms}>
              <DSListTile
                onClick={() => onChange({milestoneId: ms.id})}
                active={ms === card.milestone}
              >
                <Row
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  size={14}
                  lineHeight="16px"
                  sp="8px"
                  align="center"
                >
                  <ThemedMilestoneIcon
                    theme={setMsThemeColor(ms)}
                    size={16}
                    inline
                    milestoneState={getMilestoneState(root.account, ms)}
                  />
                  <Box size={12} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                    {ms.name}
                  </Box>
                  <Box ml="auto" size={12} flex="none" weight="normal">
                    {prettyShortWorkdays({
                      targetDay: msToDate(ms),
                      workdays: root.account.workdays,
                    })}
                  </Box>
                </Row>
              </DSListTile>
            </MilestoneTooltipForChild>
          ))}
        </Col>
      )}
    </Col>
  );
};
export default MilestonePropPicker;
